import { useEffect, useState } from "react";
import { FaSave } from "react-icons/fa";
import {
  FaChevronLeft,
  FaChevronRight,
  FaDeleteLeft,
  FaPlus,
} from "react-icons/fa6";
import { Link, useNavigate, useParams } from "react-router-dom";
import apiClient from "../../../api-services/ApiClient";
import Preloader from "../../../app-components/preloader/Preloader";
import SafeHtmlComponent from "../../../app-components/safehtml/SafeHtmlComponent";
import {
  DefectCriteriaInterface,
  InspectionFormSegmentsInterface,
  PrioritiesInterface,
  SegmentInterface,
  SegmentQuestionInterface,
} from "../../../interface-and-types/inspection.schema";
import { ToastContainer, toast } from "react-toastify";
import { LuArrowLeft, LuInfo } from "react-icons/lu";
import { SITELINKS } from "../../../app-routes/links";
import { confirmAlert } from "../../../helpers/SweetAlert";
import { notifyError } from "../../../app-components/toast/toast";

const MarineInServiceForm = () => {
  const navigate = useNavigate();
  const { id: inspectionFormId } = useParams();
  const [loading, setLoading] = useState(false);
  const [isLocalData, setIsLocalData] = useState(false);
  const [isOfflineSynced, setOfflineSynced] = useState(false);
  const [sectionId, setSectionId] = useState(null);
  const [vesselName, setVesselName] = useState(null);
  const [vesselStatus, setVesselStaus] = useState(null);
  const [currentSegment, setCurrentSegment] = useState<number>(22);
  const [priorities, setPriorities] = useState<PrioritiesInterface[]>([]);
  const [defectType, setDefectType] = useState<any[]>([]);
  const [segment, setSegment] = useState<SegmentInterface[]>([]);
  const [originalSegment, setOriginalSegment] = useState<SegmentInterface[]>(
    []
  );
  const [criticalEquipmentTypes, setCriticalEquipmentTypes] = useState<any[]>(
    []
  );
  const [equipmentList, setEquipmentList] = useState<any[]>(
    []
  );
  const [defectFormRows, setDefectFormRows] = useState<
    DefectCriteriaInterface[]
  >([]);

  useEffect(() => {
    getInspectionById(inspectionFormId ?? "");
  }, [inspectionFormId]);

  useEffect(() => {
    getPriorities();
    getSegment();
    getCriticalEquipmentTypes();
    getDefectType();
    getEquipmentList();

    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const isLocalInspectionFormId = localStorage.getItem("inspectionFormId");
    setIsLocalData(false);
    if (isLocalInspectionFormId) {
      const ls = localStorage.getItem("segments");
      if (
        ls !== JSON.stringify(segment) &&
        inspectionFormId === isLocalInspectionFormId
      ) {
        setIsLocalData(true);
      }
    }

    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (currentSegment !== null) {
      getSubmittedSegmentById(segment);
    }
    // eslint-disable-next-line
  }, [currentSegment]);

  const handleAddRow = () => {
    const data = [...segment];

    // Get the current segment and ensure it's properly typed
    const currentSegmentQuestions = [
      ...(data[currentSegment]?.segmentQuestions || []),
    ];

    // Define the new segment question
    const segment_question = {
      name: "",
      question: "",
      questionNumber: 0,
      questionType: "Text",
      id: "",
      createdAt: "",
      updatedAt: "",
      modifiedAt: "",
      createdBy: "",
      answer: "",
      comment: null,
      priority: null,
    };

    // Push the new question to the current segment
    currentSegmentQuestions.push(segment_question);

    // Update the current segment in the raw array
    data[currentSegment] = {
      ...data[currentSegment], // Preserve other properties of the current segment
      segmentQuestions: currentSegmentQuestions,
    };

    // Update state with the modified raw array
    setSegment(data);
  };

  const handleInputChange = <T extends keyof SegmentQuestionInterface>(
    index: number,
    field: T,
    value: SegmentQuestionInterface[T]
  ) => {
    const updatedSegments = [...segment];

    // Get the current segment and ensure it exists
    const currentSegmentInfo = updatedSegments[currentSegment];
    if (!currentSegmentInfo) {
      console.error("Current segment not found.");
      alert("info not accurate");
      return;
    }

    // Ensure segmentQuestions is an array and make a copy of it
    const currentSegmentQuestions = [
      ...(currentSegmentInfo.segmentQuestions || []),
    ];

    // Check if the specified question exists in the array
    const currentQuestionInSegment = currentSegmentQuestions[index];
    if (!currentQuestionInSegment) {
      console.error(`Question at index ${index} not found in segment.`);
      alert("info not accurate");
      return;
    }

    // Update the specific field in the question
    const updatedQuestion = { ...currentQuestionInSegment, [field]: value };

    // Replace the old question with the updated one
    currentSegmentQuestions[index] = updatedQuestion;

    // Update the segment with the modified questions
    updatedSegments[currentSegment] = {
      ...currentSegmentInfo, // Preserve other properties
      segmentQuestions: currentSegmentQuestions,
    };

    // Update the state
    setSegment(updatedSegments);
  };

  const handleAddDefectFormRow = () => {
    setDefectFormRows([
      ...defectFormRows,
      {
        equipmentId: "",
        criticalEquipment: "",
        defectTypeId: "",
        comments: "",
        priority: "",
        defectCheckListAttachments: [],
      },
    ]);
  };

  const handleRemoveDefectFormRow = (index: number) => {
    const updatedRecord = defectFormRows.filter(
      (_: any, i: number) => i !== index
    );
    setDefectFormRows(updatedRecord);
  };

  const handleDefectFormInputChange = <T extends keyof DefectCriteriaInterface>(
    index: number,
    field: T,
    value: DefectCriteriaInterface[T]
  ) => {
    const newRows = [...defectFormRows];
    newRows[index][field] = value;
    setDefectFormRows(newRows);
  };

  // Function to convert a single file to Base64
  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });
  };

  // Function to handle file selection
  const handleFileChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    const files = event.target.files;
    const newRows = [...defectFormRows];

    if (files) {
      const filesArray = Array.from(files);

      const base64Promises = filesArray.map(async (file) => {
        const base64 = await fileToBase64(file);
        return {
          base64Image: base64,
          mimeType: file.type,
        };
      });

      // Convert all files to Base64
      Promise.all(base64Promises).then((base64Arr: any) => {
        newRows[index]["defectCheckListAttachments"] = base64Arr;
        setDefectFormRows(newRows);
      });
    }
  };

  // get Inspection By Id
  const getInspectionById = async (inspectionFormId: string) => {
    setLoading(true);
    try {
      const res: any = await apiClient.get(
        `InspectionForm/${inspectionFormId}/User`
      );
      setVesselName(res.data?.data?.vessel?.name);
      setVesselStaus(res.data?.data?.formState);
    } catch (error) {
      console.error("Error fetching Inspection Form:", error);
    } finally {
      setLoading(false);
    }
  };

  // getSegment
  const getSegment = async () => {
    setLoading(true);
    try {
      const res: any = await apiClient.get("Segment");
      const data = res.data.data;

      const sortedData = data.sort(
        (a: any, b: any) => a.segmentNumber - b.segmentNumber
      );

      // remove Additional Area of Assessment from the sorted list
      const segmentForm = sortedData.filter(
        (item: any) => item.name !== "Additional Area of Assessment"
      );

      const additionalAssessment = sortedData.find(
        (item: any) => item.name === "Additional Area of Assessment"
      );

      segmentForm.push(additionalAssessment);

      // store the initial state in store;
      setSegment(segmentForm);
      setOriginalSegment(segmentForm); //!what is the use of this

      // get segment by
      await getSubmittedSegmentById(segmentForm);
    } catch (error) {
      setLoading(false);
      console.error(error);
    }
  };

  // get the user filled details
  const getSubmittedSegmentById = async (segmentResponse: any) => {
    setLoading(true);

    try {
      const segmentInfo = segmentResponse[currentSegment];
      if (!segmentInfo) return;

      //# get the current segment id
      const segmentId = segmentInfo.id;

      //# Ajax call
      const res: any = await apiClient.get(
        `InspectionForm/${inspectionFormId}/FormSegment/${segmentId}/Section`
      );

      //# ajax data
      const data = res.data?.data;

      // submitted segment
      setSectionId(data?.id);
      const inspectionFormSegment = data?.inspectionFormSegments ?? [];

      let updatedSegmentQuestions = [];
      if (inspectionFormSegment.length > 0) {
        updatedSegmentQuestions = inspectionFormSegment.map((item: any) => {
          return {
            ...item,
            answer: item.suitabilityStatus,
            comment: item.commentsOrFinds,
            priority: item.priority,
          };
        });
      } else {
        updatedSegmentQuestions = segmentInfo.segmentQuestions;
      }

      segmentInfo.segmentQuestions = updatedSegmentQuestions;
      segmentResponse[currentSegment] = segmentInfo;
      const vesselDefectCheckLists = data?.vesselDefectCheckLists ?? [];

      let updatedVesselDefectCheckLists = [];
      if (vesselDefectCheckLists.length > 0) {
        updatedVesselDefectCheckLists = vesselDefectCheckLists.map(
          (item: any) => {
            return {
              ...item,
              equipmentName: item?.equipmentName || "",
              equipmentId: item?.equipment.id || "",
              criticalEquipment:
                item?.criticalEquipment === "Yes"
                  ? "1"
                  : item?.criticalEquipment === "No"
                  ? "2"
                  : null,
              defectTypeId: item?.defectTypeId || null,
              comments: item?.comments || null,
              priority: item?.priorityId || null,
              defectCheckListAttachmentsList:
                item?.defectCheckListAttachments || [],
            };
          }
        );
      }

      setDefectFormRows(updatedVesselDefectCheckLists);

      if (data) {
        setSegment(segmentResponse);
      } else {
        syncOffline();
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // getPriorities
  const getPriorities = async () => {
    setLoading(true);
    try {
      const res: any = await apiClient.get("Constants/Priorities");
      setPriorities(res.data.data);
    } catch (error) {
      console.error("Error fetching Inspection Form:", error);
    } finally {
      setLoading(false);
    }
  };

  // getCriticalEquipmentTypes
  const getCriticalEquipmentTypes = async () => {
    setLoading(true);
    try {
      const res: any = await apiClient.get("Constants/CriticalEquipmentTypes");
      setCriticalEquipmentTypes(res.data.data);
    } catch (error) {
      console.error("Error fetching Inspection Form:", error);
    } finally {
      setLoading(false);
    }
  };

  // getCriticalEquipmentTypes
  const getEquipmentList = async () => {
    setLoading(true);
    try {
      const res: any = await apiClient.get("Equipment");
      setEquipmentList(res.data.data);
    } catch (error) {
      console.error("Error fetching", error);
    } finally {
      setLoading(false);
    }
  };

  // getDefectType
  const getDefectType = async () => {
    setLoading(true);
    try {
      const res: any = await apiClient.get("DefectType");
      setDefectType(res.data.data.filter((item: any) => !item.isDeleted));
    } catch (error) {
      console.error("Error fetching Inspection Form:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleFinalSubmission = async () => {
    if (vesselStatus === "Completed") return;

    try {
      setLoading(true);
      await apiClient.put(`InspectionForm/${inspectionFormId}/FormState`, {
        formState: 2,
      });

      toast.success("The inspection form submitted successfully", {
        autoClose: 60000,
        theme: "colored",
      });

      localStorage.clear();

      navigate(SITELINKS.INSPECTION_MARINESUPERINTENDENT);
    } catch (error) {
      console.log(error);

      toast.error(JSON.stringify(error), {
        autoClose: 60000,
        theme: "colored", // Options: "light", "dark", "colored"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (page?: any) => {
    if (vesselStatus === "Completed") {
      setCurrentSegment(page);
      return;
    }

    const segmentInfo = segment[currentSegment];

    // populate default insp
    const segmentQuestions = [{
        question: 'Nil',
        questionNumber: 1,
        questionType: 'text',
        answer: null,
        priority: null,
        comment: "",
    }] //segmentInfo.segmentQuestions;


    // const str_original = JSON.stringify(originalSegment);
    // const str_updated = JSON.stringify(segment);

    // if (
    //   segment.length === currentSegment + 1 &&
    //   (str_original === str_updated || segmentQuestions.length === 0)
    // ) {
    //   await handleFinalSubmission();
    //   return;
    // }

    try {
      setLoading(true);

      // const inspectionFormSegments: any[] = segmentQuestions.map((item) => ({
      //   question: item.question,
      //   questionNumber: item.questionNumber,
      //   questionType: item.questionType,
      //   suitabilityStatus: item.answer ?? null,
      //   priority: item.priority ?? null,
      //   commentsOrFinds: item.comment ?? "",
      // }));

      const inspectionFormSegments: any[] = segmentQuestions.map((item) => ({
        question: item.question,
        questionNumber: item.questionNumber,
        questionType: item.questionType,
        suitabilityStatus: item.answer ?? null,
        priority: item.priority ?? null,
        commentsOrFinds: item.comment ?? "",
      }));

      const data: any = {
        inspectionFormId: inspectionFormId,
        segmentId: segmentInfo.id,
        inspectionFormSegments: inspectionFormSegments,
        vesselDefectCheckLists: defectFormRows,
      };

      await apiClient.post("InspectionForm/Section", data);

      setDefectFormRows([
        {
          equipmentId: "",
          criticalEquipment: "",
          defectTypeId: "",
          comments: "",
          priority: "",
          defectCheckListAttachments: [],
        },
      ]);

      if (segment.length - 1 !== currentSegment) {
        setCurrentSegment(page ?? currentSegment + 1);
      }

      if (segment.length === currentSegment + 1) {
        await handleFinalSubmission();
        return;
      }
    } catch (error: any) {
      notifyError(JSON.stringify(error));

      // toast.error(JSON.stringify(error), {
      //   autoClose: 60000,
      //   theme: "colored",
      // });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (page?: any) => {
    if (vesselStatus === "Completed") {
      setCurrentSegment(page);
      return;
    }

    const segmentInfo = segment[currentSegment];
    const segmentQuestions = segmentInfo.segmentQuestions;

    const str_original = JSON.stringify(originalSegment);
    const str_updated = JSON.stringify(segment);

    if (
      segment.length === currentSegment + 1 &&
      (str_original === str_updated || segmentQuestions.length === 0)
    ) {
      await handleFinalSubmission();
      return;
    }

    // if (str_original === str_updated || segmentQuestions.length === 0) {
    //   // toast.info("Nothing to submit!");
    //   return;
    // }

    try {
      setLoading(true);

      const inspectionFormSegments: any[] = segmentQuestions.map((item) => ({
        question: item.question,
        questionNumber: item.questionNumber,
        questionType: item.questionType,
        suitabilityStatus: item.answer ?? null,
        priority: item.priority ?? null,
        commentsOrFinds: item.comment ?? "",
        id: item.id ?? null,
      }));

      const data: any = {
        inspectionFormId: inspectionFormId,
        inspectionFormSegments: inspectionFormSegments,
        vesselDefectCheckLists: defectFormRows,
      };

      await apiClient.patch(`InspectionForm/Section/${sectionId}`, data);
      // setOriginalSegment(segment);
      setDefectFormRows([
        {
          equipmentId: "",
          criticalEquipment: "",
          defectTypeId: "",
          comments: "",
          priority: "",
          defectCheckListAttachments: [],
        },
      ]);

      if (segment.length - 1 !== currentSegment) {
        setCurrentSegment(page ?? currentSegment + 1);
      }

      if (segment.length === currentSegment + 1) {
        await handleFinalSubmission();
        return;
      } else {
        // toast.success("successful!", {
        //   autoClose: 60000,
        //   theme: "colored", // Options: "light", "dark", "colored"
        // });
      }
    } catch (error: any) {
      toast.error(JSON.stringify(error), {
        autoClose: 60000,
        theme: "colored", // Options: "light", "dark", "colored"
      });
    } finally {
      setLoading(false);
    }
  };

  const prevPage = async () => {
    const currentSegmentQuestion = segment[currentSegment].segmentQuestions;

    let isValidated = currentSegmentQuestion.every(
      (q) => !q.answer || !q.comment || !q.priority
    );
    let isValidated2 = currentSegmentQuestion.every(
      (q) => !q.answer && !q.comment && !q.priority
    );
    let isValidated3 = currentSegmentQuestion.every(
      (q) => q.answer === "NA" && q.comment === "" && !q.priority
    );
    let isValidated4 = currentSegmentQuestion.every(
      (q) => q.answer !== "NO" // Allowing if q.answer is not "NO"
    );

    if (
      isValidated4 ||
      (isValidated && isValidated2) ||
      isValidated === isValidated2 ||
      isValidated3
    ) {
      sectionId
        ? handleUpdate(currentSegment - 1)
        : handleSubmit(currentSegment - 1);
    } else {
      notifyError("Comment and priority are required");
    }
  };

  const nextPage = async () => {
    const currentSegmentQuestion = segment[currentSegment].segmentQuestions;

    let isValidated = currentSegmentQuestion.every(
      (q) => !q.answer || !q.comment || !q.priority
    );
    let isValidated2 = currentSegmentQuestion.every(
      (q) => !q.answer && !q.comment && !q.priority
    );
    let isValidated3 = currentSegmentQuestion.every(
      (q) => q.answer === "NA" && q.comment === "" && !q.priority
    );
    let isValidated4 = currentSegmentQuestion.every(
      (q) => q.answer !== "NO" // Allowing if q.answer is not "NO"
    );

    if (
      isValidated4 ||
      (isValidated && isValidated2) ||
      isValidated === isValidated2 ||
      isValidated3
    ) {
      sectionId
        ? handleUpdate(currentSegment + 1)
        : handleSubmit(currentSegment + 1);
    } else {
      notifyError("Comment and priority are required");
    }
  };

  const sectionDropdown = (page: any) => {
    setCurrentSegment(page);
  };

  const saveForOffline = () => {
    // set all assets for offline:
    localStorage.clear();

    // 1. inspection ID
    localStorage.setItem("inspectionFormId", inspectionFormId ?? "");
    localStorage.setItem("vesselName", vesselName ?? "");

    // 2. segment data;
    const stringified_segment = JSON.stringify(segment);
    localStorage.setItem("segments", stringified_segment ?? "");

    // 3. priority data;
    const prioritiesStr = JSON.stringify(priorities);
    const criticalEquipmentTypesStr = JSON.stringify(criticalEquipmentTypes);
    const defectTypeStr = JSON.stringify(defectType);
    localStorage.setItem("priorities", prioritiesStr ?? "");
    localStorage.setItem(
      "criticalEquipmentTypes",
      criticalEquipmentTypesStr ?? ""
    );
    localStorage.setItem("defectType", defectTypeStr ?? "");

    setofflineTimer();
  };

  const syncOffline = () => {
    const segmentsLS = JSON.parse(localStorage.getItem("segments") || "");
    if (segmentsLS.length > 0 && isLocalData) {
      setSegment(segmentsLS ?? []);

      if (!isOfflineSynced) {
        setofflinesyncTimer();
      }
    }
  };

  const setofflineTimer = () => {
    const progressText = document.getElementById(
      "progressText"
    ) as HTMLSpanElement | null;
    const progressBar = document.getElementById(
      "progressBar"
    ) as HTMLDivElement | null;
    const progressCircle = document.getElementById(
      "progressCircle"
    ) as SVGCircleElement | null;

    if (progressText && progressCircle && progressBar) {
      let progress = 0;
      progressBar.style.display = "block";
      const interval = setInterval(() => {
        progress += 1; // Increment progress
        progressText.textContent = `${progress}%`; // Update text
        progressCircle.style.strokeDashoffset = (100 - progress).toString(); // Update circular progress

        if (progress >= 100) {
          toast.success("Form saved for offline mode!", {
            autoClose: 120000,
            theme: "colored", // Options: "light", "dark", "colored"
          });
          clearInterval(interval);

          progressBar.style.display = "none";
        }
      }, 30);
    } else {
      console.error("One or more DOM elements were not found.");
    }
  };

  const setofflinesyncTimer = () => {
    const progressText = document.getElementById(
      "progressText"
    ) as HTMLSpanElement | null;
    const progressBar = document.getElementById(
      "progressBar"
    ) as HTMLDivElement | null;
    const progressCircle = document.getElementById(
      "progressCircle"
    ) as SVGCircleElement | null;

    if (progressText && progressCircle && progressBar) {
      let progress = 0;
      progressBar.style.display = "block";
      const interval = setInterval(() => {
        progress += 1; // Increment progress
        progressText.textContent = `${progress}%`; // Update text
        progressCircle.style.strokeDashoffset = (100 - progress).toString(); // Update circular progress

        if (progress >= 100) {
          toast.success("Offline data synced successfully!", {
            autoClose: 5000,
            theme: "colored", // Options: "light", "dark", "colored"
          });
          clearInterval(interval);

          progressBar.style.display = "none";

          setOfflineSynced(true);
        }
      }, 50);
    } else {
      console.error("One or more DOM elements were not found.");
    }
  };

  const handleOfflineSubmit = async (page: number) => {
    const segmentInfo = segment[page];
    const segmentQuestions = segmentInfo.segmentQuestions;

    const str_original = JSON.stringify(originalSegment[page]);
    const str_updated = JSON.stringify(segment[page]);

    if (str_original === str_updated || segmentQuestions.length === 0) {
      if (segment.length > page + 1) {
        handleOfflineSubmit(page + 1);
      }
      if (segment.length === page + 1) {
        await handleFinalSubmission();
      }
      return;
    }

    try {
      setLoading(true);
      console.log(page);

      const inspectionFormSegments: InspectionFormSegmentsInterface[] =
        segmentQuestions.map((item) => ({
          question: item.question,
          questionNumber: item.questionNumber,
          questionType: item.questionType,
          suitabilityStatus: item.answer ?? "",
          priority: item.priority ?? "",
          commentsOrFinds: item.comment ?? "",
        }));

      const data: any = {
        inspectionFormId: inspectionFormId,
        segmentId: segmentInfo.id,
        inspectionFormSegments: inspectionFormSegments,
        vesselDefectCheckLists: defectFormRows,
      };

      await apiClient.post("InspectionForm/Section", data);

      setDefectFormRows([
        {
          equipmentId: "",
          criticalEquipment: "",
          defectTypeId: "",
          comments: "",
          priority: "",
          defectCheckListAttachments: [],
        },
      ]);

      if (segment.length > page + 1) handleOfflineSubmit(page + 1);

      if (segment.length === page + 1) {
        await handleFinalSubmission();
      }
    } catch (error: any) {
      toast.error(JSON.stringify(error), {
        autoClose: 60000,
        theme: "colored",
      });
    } finally {
      setLoading(false);
    }
  };

  const submitOfflineSync = async () => {
    const canSubmit = await confirmAlert(
      "Are you sure you want to submit offline data without previewing?"
    );

    if (canSubmit.isConfirmed) {
      handleOfflineSubmit(0);
    }
  };

  const downloadFile = async () => {
    try {
      setLoading(true);
      await apiClient
        .get(`InspectionForm/${inspectionFormId}/Download`)
        .then((res) => {
          console.log(res);
          var a = document.createElement("a");
          a.href = "data:image/png;base64," + res.data.data.base64String;
          a.download = res.data.data.fileName; // res.data.data.attachmentType.name+'-'+res.data.data.name
          a.click();

          setLoading(false);
        })
        .catch((err) => {
          console.log(err);
          setLoading(false);
        });
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <section className="flex justify-between items-center bg-gradient-to-r from-secondaryBlue to-primaryBlue shadow-md border-b border-gray-200">
        <div className="xl:container xl:mx-auto md:px-4 py-3 pb-24 flex justify-between items-center w-full">
          {/* page-name / form name */}
          <div className="font-medium lg:text-sm text-gray-100 flex gap-3 items-center">
            <Link
              to={SITELINKS.INSPECTION_MARINESUPERINTENDENT}
              className="text-[20px]"
            >
              <LuArrowLeft />
            </Link>
            <h4>
              In-Service Form / <span>{vesselName}</span>
            </h4>
          </div>

          <div
            className="flex flex-col items-center"
            id="progressBar"
            style={{ display: "none" }}
          >
            <div className="relative w-7 h-7 flex items-center justify-center">
              <svg
                className="w-full h-full transform -rotate-90"
                viewBox="0 0 36 36"
              >
                <circle
                  className="text-gray-400"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="transparent"
                  r="16"
                  cx="18"
                  cy="18"
                />
                <circle
                  id="progressCircle"
                  className="text-blue-100"
                  stroke="currentColor"
                  strokeWidth="4"
                  strokeDasharray="100"
                  strokeDashoffset="100"
                  fill="transparent"
                  r="16"
                  cx="18"
                  cy="18"
                  style={{ transition: "strokeDashoffset 0.2s ease" }}
                />
              </svg>

              <div className="absolute top-[-1px] text-center">
                <span
                  id="progressText"
                  className="text-[8px] font-medium text-gray-100"
                >
                  0%
                </span>
              </div>
            </div>
          </div>

          <div className="flex gap-1">
            <button
              className="disabled:bg-gray-400 flex gap-2 items-center justify-start text-xs bg-slate-500 border-slate-400 shadow-sm p-1 border-2 rounded text-white font-medium px-2 hover:bg-secondaryBlue transition-colors duration-300 ease-in-out transform hidden"
              type="button"
              onClick={prevPage}
              disabled={currentSegment === 0}
            >
              <FaChevronLeft className="text-md" />
              <span>Back</span>
            </button>

            <button
              className="disabled:bg-gray-400 flex gap-2 items-center text-xs bg-slate-500 shadow p-1 border-2 border-slate-400 rounded text-white font-medium px-2 hover:bg-secondaryBlue transition-colors duration-300 ease-in-out transform hidden"
              type="button"
              onClick={nextPage}
              disabled={segment.length - 1 === currentSegment}
            >
              <span>Next</span>
              <FaChevronRight className="text-md" />
            </button>

            <div>
              {vesselStatus === "InProgress" && (
                <>
                  <div>
                    {segment.length === currentSegment + 1 && (
                      <>
                        {sectionId ? (
                          <button
                            className="flex gap-1 items-center text-xs bg-green-600 border-2 shadow p-1 rounded text-white font-medium px-2 hidden"
                            type="button"
                            onClick={handleUpdate}
                          >
                            <FaSave />
                            <span>Update</span>
                          </button>
                        ) : (
                          <button
                            className="flex gap-1 items-center text-xs bg-green-600 shadow p-1 border-2 rounded text-white font-medium px-2"
                            type="button"
                            onClick={handleSubmit}
                          >
                            <FaSave />
                            <span>Submit</span>
                          </button>
                        )}
                      </>
                    )}
                  </div>

                  <div className="hidden">
                    {!isOfflineSynced ? (
                      <>
                        {segment.length !== currentSegment + 1 && (
                          <>
                            {isLocalData ? (
                              <button
                                onClick={syncOffline}
                                className="flex gap-1 items-center text-xs border-slate-200 border-2 text-slate-100 shadow-sm p-1 rounded bg-transparent font-normal px-2 border"
                                type="button"
                                title="Template will be available for offline mode"
                              >
                                <span>Sync Offline Template</span>
                              </button>
                            ) : (
                              <button
                                onClick={saveForOffline}
                                className="flex gap-1 items-center text-xs border-slate-200 border-2 text-slate-100 shadow-sm p-1 rounded bg-transparent font-normal px-2 border"
                                type="button"
                                title="Download for offline"
                              >
                                <span>Download Offline Template</span>
                              </button>
                            )}
                          </>
                        )}
                      </>
                    ) : (
                      <>
                        {segment.length !== currentSegment + 1 && (
                          <button
                            onClick={submitOfflineSync}
                            className="flex gap-1 items-center text-xs border-slate-200 border-2 text-slate-100 shadow-sm p-1 rounded bg-green-700 font-normal px-2 border hover:bg-green-800 transition-colors duration-300 ease-in-out transform"
                            type="button"
                            title="Submit my offline template"
                          >
                            <span>Submit Synced Data</span>
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </>
              )}

              {vesselStatus === "Completed" && (
                <button
                  className="flex gap-1 items-center text-xs bg-green-600 shadow p-1 border-2 rounded text-white font-medium px-2"
                  type="button"
                  onClick={downloadFile}
                >
                  <span>FInal PDF</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* page content wrapper section */}
      <section className="pt-5 mx-auto md:px-4 xl:container relative top-[-100px] xl:top-[-105px]">
        {/* main form questionaire - sections */}
        <div className="rounded-lg bg-white border border-gray-200 mb-5 shadow-sm hidden">
          <div className="p-4 pb-8">
            {/* page / segment lead area */}
            <div className="flex justify-between text-slate-800 items-center mb-4 font-medium">
              <h1 className="font-semibold text-sm text-gray-800">
                {segment[currentSegment]?.name}{" "}
                <small className="text-orange-600 text-[9px]">
                  (*) All fields are required!
                </small>
              </h1>

              <select
                id="array-length-dropdown"
                className="text-xs border p-1 rounded"
                value={currentSegment}
                onChange={(e) => sectionDropdown(Number(e.target.value))}
              >
                {Array.from({ length: segment.length }, (_, index) => (
                  <option key={index} value={index}>
                    Section {index + 1} - {segment[index]?.name}
                  </option>
                ))}
              </select>

              <span className="bg-gray-200 text-xs rounded-full px-1">
                {currentSegment + 1} of {segment?.length}
              </span>
            </div>

            {/* form wrapper */}
            <div className="overflow-x-auto">
              <table className="table-auto w-full border-collapse border border-gray-300">
                <thead>
                  <tr className="w-full bg-gray-200 text-gray-600 capitalize text-xs leading-normal text-left font-semibold">
                    <th className="border border-gray-300 px-2 py-2 text-slate-800">
                      SN
                    </th>
                    <th className="border border-gray-300 px-2 py-2 text-slate-800">
                      {segment[currentSegment]?.name}
                    </th>
                    <th className="border border-gray-300 px-2 py-2 text-slate-800">
                      Yes
                    </th>
                    <th className="border border-gray-300 px-2 py-2 text-slate-800">
                      No
                    </th>
                    <th className="border border-gray-300 px-2 py-2 text-slate-800">
                      N/A
                    </th>
                    <th className="border border-gray-300 px-2 py-2 text-slate-800">
                      Comments
                    </th>
                    <th className="border border-gray-300 px-2 py- text-slate-800">
                      Priority
                    </th>
                  </tr>
                </thead>

                <tbody className="text-gray-800 text-xs font-normal">
                  {segment[currentSegment]?.segmentQuestions.map(
                    (row, index) => (
                      <tr key={index} className="even:bg-gray-50">
                        <td className="border border-gray-300 px-2 py-2">
                          {index + 1}
                        </td>

                        <td className="border border-gray-300 px-2 py-2">
                          {row.id ? (
                            <SafeHtmlComponent html={row.question} />
                          ) : (
                            <input
                              type="text"
                              className="w-full border border-1 p-1"
                              value={row.question}
                              onChange={(e) =>
                                handleInputChange(
                                  index,
                                  "question",
                                  e.target.value
                                )
                              }
                              placeholder="Enter Question"
                              disabled={vesselStatus === "Completed"}
                            />
                          )}
                        </td>

                        <td className="border border-gray-300 px-2 py-2">
                          <input
                            className="h-[1.125rem] w-[1.125rem] rounded-[0.25rem] border-[0.125rem] border-solid border-neutral-300"
                            type="checkbox"
                            checked={
                              row?.answer?.toUpperCase() === "YES" || false
                            }
                            onChange={(e) =>
                              handleInputChange(index, "answer", "YES")
                            }
                            disabled={vesselStatus === "Completed"}
                          />
                        </td>

                        <td className="border border-gray-300 px-2 py-2">
                          <input
                            className="h-[1.125rem] w-[1.125rem] rounded-[0.25rem] border-[0.125rem] border-solid border-neutral-300"
                            type="checkbox"
                            checked={
                              row?.answer?.toUpperCase() === "NO" || false
                            }
                            onChange={(e) =>
                              handleInputChange(index, "answer", "NO")
                            }
                            disabled={vesselStatus === "Completed"}
                          />
                        </td>

                        <td className="border border-gray-300 px-2 py-2">
                          <input
                            className="h-[1.125rem] w-[1.125rem] rounded-[0.25rem] border-[0.125rem] border-solid border-neutral-300"
                            type="checkbox"
                            checked={
                              row?.answer?.toUpperCase() === "NA" || false
                            }
                            onChange={(e) =>
                              handleInputChange(index, "answer", "NA")
                            }
                            disabled={vesselStatus === "Completed"}
                          />
                        </td>

                        <td className="border border-gray-300 px-2 py-2">
                          <input
                            type="text"
                            className="min-w-[300px] w-full border p-1 rounded"
                            value={row?.comment || ""}
                            onChange={(e) =>
                              handleInputChange(
                                index,
                                "comment",
                                e.target.value
                              )
                            }
                            placeholder="Add comments"
                            disabled={vesselStatus === "Completed"}
                          />
                        </td>

                        <td className="border border-gray-300 px-2 py-1">
                          <select
                            className="w-full border py-1 hover:border-slate-500 rounded p-1 w-[100px]"
                            value={row?.priority || ""}
                            onChange={(e) =>
                              handleInputChange(
                                index,
                                "priority",
                                e.target.value
                              )
                            }
                            disabled={vesselStatus === "Completed"}
                          >
                            <option value="">Select</option>
                            {priorities.map((priority, i) => {
                              return (
                                <option key={i} value={priority?.value}>
                                  {priority?.name} ({priority?.value})
                                </option>
                              );
                            })}
                          </select>
                        </td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            </div>

            {/* add new row action */}
            {vesselStatus !== "Completed" && (
              <>
                {segment.length === currentSegment + 1 && (
                  <button
                    className="mt-4 text-xs flex gap-2 items-center px-3 py-2 bg-red-600 text-white rounded hover:bg-red-600"
                    onClick={handleAddRow}
                  >
                    <FaPlus />
                    <span>Add New Row</span>
                  </button>
                )}
              </>
            )}
          </div>
        </div>

        {/* Defect form criteria */}
        <div className="rounded-lg bg-white border border-gray-200 mb-10 shadow-sm">
          <div className="p-4">
            <div className="flex justify-between text-slate-800 items-center mb-4 font-medium">
              <h1 className="text-sm">
                Defect Form Criteria{" "}
                <small className="text-orange-600text-xs">
                  (*) All fields are required!
                </small>
              </h1>
            </div>

            <div className="overflow-x-auto">
              <table className="table-auto w-full border-collapse border border-gray-300">
                <thead>
                  <tr className="bg-gray-200 text-left text-xs">
                    <th className="border border-gray-300 px-2 py-2 font-medium text-slate-800  align-top">
                      SN
                    </th>
                    <th className="border border-gray-300 px-2 py-2 font-medium text-slate-800  align-top">
                      Equipment Name <b className="text-red-600">*</b>
                    </th>
                    <th className="border border-gray-300 px-2 py-2 font-medium text-slate-800  align-top">
                      Critical Equipment
                    </th>
                    <th className="border border-gray-300 px-2 py-2 font-medium text-slate-800 align-top">
                      Type of Defect
                    </th>
                    <th className="border border-gray-300 px-2 py-2 font-medium text-slate-800 align-top">
                      Comments
                    </th>
                    <th className="border border-gray-300 px-2 py-2 font-medium text-slate-800 align-top">
                      <span className="flex gap-2">
                        <span>Priority </span>{" "}
                        <LuInfo
                          className="text-orange-600"
                          title="Priority 0 (P0): Urgent actions related to critical defects requiring immediate closure.
  Priority 1 (P1): High-priority actions related to major defects with a closure timeframe of 48 hours to 5 days. – we are to disregard the timeframe in this priority list and retain allowing the Vessel Operator input due date. The due date can however be overridden by the Marine Superintendent with a comment to the Vessel Operator.
  Priority 2 (P2): Medium priority actions related to minor defects with a closure timeframe of, for example, 14 days.
  Priority 3 (P3): Low-priority actions related to routine maintenance and general improvements not necessarily linked to vessel compliance."
                        />
                      </span>
                    </th>
                    <th className="border border-gray-300 px-2 py-2 font-medium text-slate-800 align-top">
                      Attach Picture
                    </th>
                    <th className="border border-gray-300 px-2 py-2 font-medium text-slate-800 align-top">
                      Action
                    </th>
                  </tr>
                </thead>

                <tbody className="text-xs">
                  {defectFormRows.length > 0 &&
                    defectFormRows.map((row, index) => (
                      <tr key={index} className="even:bg-gray-50">
                        <td className="border border-gray-300 px-2 py-1 align-top">
                          {index + 1}
                        </td>

                        <td className="border border-gray-300 px-2 py-1 align-top">
                          {/* <input
                            type="text"
                            className="w-full min-w-[240px] border  p-1"
                            value={row.equipmentName}
                            onChange={(e) =>
                              handleDefectFormInputChange(
                                index,
                                "equipmentName",
                                e.target.value
                              )
                            }
                            placeholder="Enter Equipment Name"
                          /> */}

                          <select
                            className="w-full border py-1 hover:border-slate-500 rounded p-1"
                            value={row?.equipmentId || ""}
                            onChange={(e) =>
                              handleDefectFormInputChange(
                                index,
                                "equipmentId",
                                e.target.value
                              )
                            }
                          >
                            <option value="">Select</option>
                            {equipmentList.map((item, i) => {
                              return (
                                <option key={i} value={item?.id}>
                                  {item?.name}
                                </option>
                              );
                            })}
                          </select>
                        </td>

                        <td className="border border-gray-300 px-2 py-1 align-top">
                          <select
                            className="w-full border hover:border-slate-500 rounded p-1 py-1"
                            value={row?.criticalEquipment || ""}
                            onChange={(e) =>
                              handleDefectFormInputChange(
                                index,
                                "criticalEquipment",
                                e.target.value
                              )
                            }
                          >
                            <option value="">Select</option>
                            {criticalEquipmentTypes.map((item, i) => {
                              return (
                                <option key={i} value={item?.id}>
                                  {item?.name}
                                </option>
                              );
                            })}
                          </select>
                        </td>

                        <td className="border border-gray-300 px-2 py-1 align-top">
                          <select
                            className="w-full border py-1 hover:border-slate-500 rounded p-1"
                            value={row?.defectTypeId || ""}
                            onChange={(e) =>
                              handleDefectFormInputChange(
                                index,
                                "defectTypeId",
                                e.target.value
                              )
                            }
                          >
                            <option value="">Select</option>
                            {defectType.map((item, i) => {
                              return (
                                <option key={i} value={item?.id}>
                                  {item?.name}
                                </option>
                              );
                            })}
                          </select>
                        </td>

                        <td className="border border-gray-300 px-2 py-1 align-top">
                          <input
                            className="min-w-[300px] w-full border p-1 "
                            value={row?.comments || ""}
                            onChange={(e) =>
                              handleDefectFormInputChange(
                                index,
                                "comments",
                                e.target.value
                              )
                            }
                            placeholder="Add comments"
                          />
                        </td>

                        <td className="border border-gray-300 px-2 py-1 align-top">
                          <select
                            className="w-full border py-1 hover:border-slate-500 rounded p-1 w-[100px]"
                            value={row?.priority || ""}
                            onChange={(e) =>
                              handleDefectFormInputChange(
                                index,
                                "priority",
                                e.target.value
                              )
                            }
                          >
                            <option value="">Select</option>
                            {priorities.map((priority, i) => {
                              return (
                                <option key={i} value={priority?.value}>
                                  {priority?.name} ({priority?.value})
                                </option>
                              );
                            })}
                          </select>
                        </td>

                        <td className="border border-gray-300 px-2 py-1 w-[160px] align-top">
                          <input
                            type="file"
                            id="file-upload"
                            multiple
                            accept="image/*"
                            className="border p-1 w-[160px]"
                            onChange={(e) => handleFileChange(e, index)}
                          />
                          <div className="flex gap-2 flex-grow mt-1">
                            {row?.defectCheckListAttachments.map(
                              (item: any, index: number) => (
                                <img
                                  alt="ItemAttached"
                                  src={
                                    item.base64Image.includes("data:image/")
                                      ? item.base64Image
                                      : `data:image/png;base64,${item.base64Image}`
                                  }
                                  key={index}
                                  className="pb-1 h-[40px] rounded bg-slate-50"
                                  title={item.base64Image}
                                />
                              )
                            )}
                          </div>
                        </td>

                        <td className="border border-gray-300 px-2 py-1 text-center align-top">
                          <button
                            className="text-3xl text-red-500 hover:text-red-600"
                            onClick={() => handleRemoveDefectFormRow(index)}
                          >
                            <FaDeleteLeft />
                          </button>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>

              {defectFormRows.length === 0 && (
                <div className="p-10 text-center text-slate-600 text-xs border">
                  No record, click add new to{" "}
                  <button
                    onClick={handleAddDefectFormRow}
                    className="text-red-600"
                  >
                    add a new defect
                  </button>
                </div>
              )}
            </div>

            {defectFormRows.length > 0 && (
              <button
                className="mt-4 text-xs flex gap-2 items-center px-3 py-2 bg-slate-500 text-white rounded hover:bg-red-600"
                onClick={handleAddDefectFormRow}
              >
                <FaPlus />
                <span>Add New Defect</span>
              </button>
            )}
          </div>
        </div>
      </section>

      <ToastContainer position="bottom-left" hideProgressBar />
      <Preloader loading={loading} />
    </>
  );
};

export default MarineInServiceForm;
